import { DateTime } from "luxon";
import { useState, useMemo } from "react";

import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
// import { Calendar } from "../components/common/Calendar";
import { Textarea } from "../components/common/Textarea";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import { Dialog, DialogContent, DialogTrigger, DialogClose, DialogTitle } from "../components/common/Dialog";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";

import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { formatDateDMY, formatDateTimeDMY } from "../lib/ScheduleUtils";

import {
  ArrowLeft,
  FileText,
  Calendar,
  User,
  ImageIcon,
  Stethoscope,
  Loader2,
  ChevronRight,
  Clock,
  Info,
  X,
  Plus,
  ScanLine,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import useBrandTranslation from "../hooks/useBrandTranslation";

function calcAge(dateStr) {
  if (!dateStr) return null;
  const birth = new Date(dateStr);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

const GENDER_LABELS = { male: "Masculino", female: "Femenino", other: "Otro", prefer_not: "Prefiere no decir" };
const YES_NO = { yes: "Sí", no: "No" };

function PatientInfoDialog({ patient }) {
  const { t } = useBrandTranslation();
  if (!patient) return null;

  const age = calcAge(patient.birth_date);
  const bloodFull = [patient.blood_type, patient.blood_factor].filter(Boolean).join("") || null;

  const sections = [
    {
      title: "Datos Personales",
      items: [
        { label: "DNI", value: patient.dni },
        { label: "Género", value: GENDER_LABELS[patient.gender] },
        { label: "Fecha de Nacimiento", value: patient.birth_date ? formatDateDMY(patient.birth_date) : null },
        { label: "Edad", value: age != null ? `${age} años` : null },
      ],
    },
    {
      title: "Obra Social",
      items: [
        { label: "Obra Social / Prepaga", value: patient.health_insurance_name },
        { label: "Nº Afiliado", value: patient.health_insurance_number },
        { label: "Plan", value: patient.health_insurance_plan },
      ],
    },
    {
      title: "Datos de Salud",
      items: [
        { label: "Grupo Sanguíneo", value: bloodFull },
        { label: "Tabaquismo", value: YES_NO[patient.smoking] },
        { label: "Sedentarismo", value: YES_NO[patient.sedentary] },
        { label: "Tóxicos", value: YES_NO[patient.toxics] },
      ],
    },
    {
      title: "Antecedentes Médicos",
      items: [
        { label: "", value: patient.medical_antecedents, full: true },
      ],
    },
    {
      title: "Discapacidad",
      items: [
        { label: "Tipo", value: patient.disability && patient.disability !== "none" ? patient.disability : null },
        { label: "Detalle", value: patient.disability_detail },
      ],
    },
    {
      title: "Contacto de Emergencia",
      items: [
        { label: "Nombre", value: [patient.emergency_contact_name, patient.emergency_contact_last_name].filter(Boolean).join(" ") || null },
        { label: "Teléfono", value: patient.emergency_contact_phone },
      ],
    },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full w-7 h-7 text-blue-600 hover:bg-blue-100 transition-colors"
          title={t("app.clinical.viewPatientBackground", "Ver antecedentes del paciente")}
        >
          <Info className="w-5 h-5" />
        </button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-lg max-h-[85vh] overflow-y-auto rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <DialogTitle className="text-lg font-bold text-gray-900">
            {t("app.clinical.patientBackgroundTitle", "Antecedentes del Paciente")}
          </DialogTitle>
          <DialogClose asChild>
            <button type="button" className="rounded-full p-1 hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </DialogClose>
        </div>
        <div className="space-y-4">
          {sections.map((section) => {
            const hasData = section.items.some((i) => i.value);
            if (!hasData) return null;
            return (
              <div key={section.title}>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">
                  {section.title}
                </h4>
                <div className="space-y-1">
                  {section.items.map((item, idx) => {
                    if (!item.value) return null;
                    if (item.full) {
                      return (
                        <p key={idx} className="text-sm text-gray-700 bg-gray-50 rounded p-2 whitespace-pre-wrap">
                          {item.value}
                        </p>
                      );
                    }
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 min-w-[120px]">{item.label}:</span>
                        <span className="text-gray-800 font-medium">{item.value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClinicalHistoryView({ patientId: patientIdProp, showAddButton = true, child = false }) {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clinicalHistories, profiles, loading } = useData();
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(true);
  const navigate = useNavigate();
  const patientIdFromUrl = searchParams.get("patientId");
  const patientId = patientIdProp || patientIdFromUrl;
  const appointmentId = searchParams.get("appointmentId");
  const consultationDate =
    searchParams.get("consultationDate") ||
    DateTime.now().toFormat("yyyy-MM-dd");

  const linkToAddHistory = `/doctor/clinical-history/add?${appointmentId ? `appointmentId=${appointmentId}&` : ""}patientId=${patientId}&doctorId=${user?.id}&consultationDate=${consultationDate}`;
  const linkToDigitizeHistory = `/doctor/clinical-history/add?mode=digitize&${appointmentId ? `appointmentId=${appointmentId}&` : ""}patientId=${patientId}&doctorId=${user?.id}&consultationDate=${consultationDate}`;

  // Obtener todas las historias clínicas del paciente
  const patientHistories = useMemo(() => {
    if (!patientId) return [];
    return clinicalHistories
      .filter((h) => h.patient_id === patientId)
      .sort(
        (a, b) =>
          new Date(b.consultation_date).getTime() -
          new Date(a.consultation_date).getTime()
      );
  }, [patientId, clinicalHistories]);

  // Obtener información del paciente
  const patient = useMemo(() => {
    if (!patientId) return null;
    return profiles.find((p) => p.id === patientId);
  }, [patientId, profiles]);

  // Obtener información del médico de la historia seleccionada
  const selectedDoctor = useMemo(() => {
    if (!selectedHistory?.doctor_id) return null;
    return profiles.find((p) => p.id === selectedHistory.doctor_id);
  }, [selectedHistory?.doctor_id, profiles]);

  // Verificar permisos
  const canView = useMemo(() => {
    return (
      user &&
      ((user.role === "doctor" &&
        patientHistories.some((h) => h.doctor_id === user.id)) ||
        (user.role === "patient" && user.id === patientId))
    );
  }, [user, patientHistories, patientId]);

  // Perfiles ya cargados y el paciente no está (id inválido o paciente no registrado):
  // no quedarse en el spinner para siempre.
  if (!patient && (!patientId || profiles.length > 0) && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-12">
            <User className="w-12 h-12 text-gray-300 mx-auto my-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("app.clinical.patientNotFoundTitle", "Paciente no encontrado")}
            </h3>
            <p className="text-gray-500 mb-6">
              {t(
                "app.clinical.patientNotFoundDesc",
                "No se encontró la historia clínica de este paciente. Puede que no esté registrado en el sistema."
              )}
            </p>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("common.back", "Volver")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-12">
            <Loader2 className="w-12 h-12 text-gray-600 mx-auto my-4 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Cargando Historia Clínica
            </h3>
            <p className="text-gray-500">
              Obteniendo información del paciente...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // if (!canView) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-gray-50">
  //       <Card className="w-full max-w-md">
  //         <CardHeader>
  //           <CardTitle>{t("app.appointments.restrictedAccess", "Acceso Restringido")}</CardTitle>
  //         </CardHeader>
  //         <CardContent>
  //           <p className="text-gray-600">
  //             No tienes permisos para ver esta historia clínica.
  //           </p>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  const getDoctorName = (doctorId) => {
    const doctor = profiles.find((p) => p.id === doctorId);
    return doctor ? `Dr. ${doctor.name} ${doctor.last_name}` : "Médico";
  };

  const getDoctorSpecialization = (doctorId) => {
    const doctor = profiles.find((p) => p.id === doctorId);
    return doctor?.specialization || "Medicina General";
  };

  return (
    <div className={child ? "mb-10" : "min-h-screen bg-gray-50 p-4 mb-10"}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          
          {!child && (
            <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          )}
          {!child && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Historia Clínica
              </h1>
              <div className="flex items-center gap-2 text-lg text-gray-700">
                <User className="w-5 h-5" />
                <span className="font-medium">
                  {patient.name} {patient.last_name}
                </span>
                <PatientInfoDialog patient={patient} />
              </div>
              <p className="text-sm text-gray-600">{patient.email}</p>
            </div>

            <div className="w-full sm:w-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-blue-600">
                  {patientHistories.length}
                </div>
                <div className="text-sm text-gray-600 whitespace-nowrap">
                  Consultas registradas
                </div>
              </div>
              {showAddButton && (
                <div className="flex flex-wrap gap-2 shrink-0">
                  <a href={linkToAddHistory}>
                    <Button
                      variant="default"
                      className="font-semibold rounded-full px-5 py-2 shadow-sm hover:shadow-md transition-all bg-emerald-500 hover:bg-emerald-400 text-white"
                    >
                      <Plus className="w-4 h-4" />
                      {t("app.commonUi.add", "Agregar")}
                    </Button>
                  </a>
                  <a href={linkToDigitizeHistory}>
                    <Button
                      variant="outline"
                      className="font-semibold rounded-full px-5 py-2 shadow-sm hover:shadow-md transition-all"
                    >
                      <ScanLine className="w-4 h-4" />
                      {t("app.clinical.digitizeButton", "Digitalizar Historias")}
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {patientHistories.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Sin Historia Clínica
              </h3>
              <p className="text-gray-500">
                Este paciente aún no tiene consultas registradas.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lista de historias clínicas */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Historial de Consultas
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-96 lg:max-h-[600px] overflow-y-auto">
                    {patientHistories.map((history, index) => (
                      <div
                        key={history.id}
                        className={`p-4 border-b cursor-pointer transition-all hover:bg-gray-50 ${
                          selectedHistory?.id === history.id
                            ? "bg-blue-50 border-l-4 border-l-blue-500"
                            : ""
                        }`}
                        onClick={() => setSelectedHistory(history)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="font-medium text-sm">
                                {formatDateDMY(history.consultation_date)}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                              <Stethoscope className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-600">
                                {getDoctorName(history.doctor_id)}
                              </span>
                            </div>

                            <Badge variant="outline" className="text-xs">
                              {getDoctorSpecialization(history.doctor_id)}
                            </Badge>
                          </div>

                          <ChevronRight
                            className={`w-4 h-4 text-gray-400 transition-transform ${
                              selectedHistory?.id === history.id
                                ? "rotate-90"
                                : ""
                            }`}
                          />
                        </div>

                        <div className="text-xs text-gray-500 truncate">
                          {history.notes.substring(0, 80)}...
                        </div>

                        {history.image_urls &&
                          history.image_urls.length > 0 && (
                            <div className="flex items-center gap-1 mt-2">
                              <ImageIcon className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {history.image_urls.length} imagen
                                {history.image_urls.length !== 1 ? "es" : ""}
                              </span>
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detalle de la historia seleccionada */}
            <div className="lg:col-span-2">
              {selectedHistory ? (
                <div className="space-y-6">
                  {/* Información de la consulta */}
                  <Card>
                    <CardHeader>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            Consulta del{" "}
                            {formatDateDMY(selectedHistory.consultation_date)}
                          </CardTitle>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>
                                Creado el{" "}
                                {formatDateTimeDMY(selectedHistory.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {selectedDoctor && (
                          <div className="text-right">
                            <div className="font-medium">
                              Dr. {selectedDoctor.name}{" "}
                              {selectedDoctor.last_name}
                            </div>
                            <Badge variant="outline">
                              {selectedDoctor.specialization}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Notas de la consulta */}
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("app.clinical.consultationNotes", "Notas de la Consulta")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose max-w-none">
                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                          {selectedHistory.notes}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Imágenes adjuntas */}
                  {selectedHistory.image_urls &&
                    selectedHistory.image_urls.length > 0 && (
                      <Card className="!mb-20">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <ImageIcon className="w-5 h-5" />
                            Imágenes Adjuntas (
                            {selectedHistory.image_urls.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {selectedHistory.image_urls.map((url, index) => (
                              <div key={index} className="group relative">
                                <img
                                  src={
                                    url ||
                                    "/placeholder.svg?height=256&width=256"
                                  }
                                  alt={`Imagen ${index + 1}`}
                                  className="w-full h-64 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(url, "_blank")}
                                />
                                <div
                                  className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center"
                                  onClick={() => window.open(url, "_blank")}
                                >
                                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                    Click para ampliar
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                </div>
              ) : (
                <Card className="!mb-20">
                  <CardContent className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mt-6 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 my-2">
                      Selecciona una Consulta
                    </h3>
                    <p className="text-gray-500">
                      Haz clic en una consulta del historial para ver los
                      detalles.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
